import { graphqlRequest } from "./client";
import type { StepGate } from "./types";

export interface FinanceView {
  record: {
    services: string[];
    idDocs: string[];
    moneyDocs: string[];
    hygieneAnswers: { key: string; yes: boolean }[];
    tier?: string | null;
    flags: string[];
    lmsDone: boolean;
    referredAt?: string | null;
  };
  liveTier?: string | null;
  liveFlags: string[];
  gates: StepGate[];
  doneCount: number;
  referred: boolean;
}

export type FinanceChecklist = "idDocs" | "moneyDocs";

const FINANCE_FIELDS = `record { services idDocs moneyDocs hygieneAnswers { key yes } tier flags lmsDone referredAt } liveTier liveFlags
  gates { pass items { label done detail } } doneCount referred`;

/** Finance Step 4 answers as a key -> yes/no map. */
export function hygieneAnswers(view: FinanceView): Record<string, boolean> {
  return Object.fromEntries(view.record.hygieneAnswers.map((answer) => [answer.key, answer.yes]));
}

/** The Access to Finance track. */
export async function fetchFinance(): Promise<FinanceView> {
  return (await graphqlRequest<{ getFinance: FinanceView }>(`{ getFinance { ${FINANCE_FIELDS} } }`))
    .getFinance;
}

/** Finance Step 1: services of interest. */
export async function saveFinanceServices(services: string[]): Promise<FinanceView> {
  return (
    await graphqlRequest<{ saveFinanceServices: FinanceView }>(
      `mutation($services: [String!]!) { saveFinanceServices(services: $services) { ${FINANCE_FIELDS} } }`,
      { variables: { services } },
    )
  ).saveFinanceServices;
}

/** Finance Steps 2 and 3: preparation checklists. */
export async function saveFinanceChecklist(
  list: FinanceChecklist,
  keys: string[],
): Promise<FinanceView> {
  return (
    await graphqlRequest<{ saveFinanceChecklist: FinanceView }>(
      `mutation($list: FinanceChecklist!, $keys: [String!]!) { saveFinanceChecklist(list: $list, keys: $keys) { ${FINANCE_FIELDS} } }`,
      { variables: { list, keys } },
    )
  ).saveFinanceChecklist;
}

/** Finance Step 4: readiness answers. */
export async function saveHygieneAnswers(
  answers: { key: string; yes: boolean }[],
): Promise<FinanceView> {
  return (
    await graphqlRequest<{ saveHygieneAnswers: FinanceView }>(
      `mutation($answers: [HygieneAnswerInput!]!) { saveHygieneAnswers(answers: $answers) { ${FINANCE_FIELDS} } }`,
      { variables: { answers } },
    )
  ).saveHygieneAnswers;
}

/** Finance Step 5: LMS modules self-report. */
export async function setLmsDone(done: boolean): Promise<FinanceView> {
  return (
    await graphqlRequest<{ setLmsDone: FinanceView }>(
      `mutation($done: Boolean!) { setLmsDone(done: $done) { ${FINANCE_FIELDS} } }`,
      { variables: { done } },
    )
  ).setLmsDone;
}

/** Complete a finance step. Step 6 is the referral handover. */
export async function completeFinanceStep(step: number): Promise<FinanceView> {
  return (
    await graphqlRequest<{ completeFinanceStep: FinanceView }>(
      `mutation($step: Int!) { completeFinanceStep(step: $step) { ${FINANCE_FIELDS} } }`,
      { variables: { step } },
    )
  ).completeFinanceStep;
}
